import type { UnspecifiedReferralFormState } from '../unspecifiedReferralFormData'
import type { OrderPdfData, OrderPdfSection, AutoPopulateData } from './pdfTypes'
import { referralClosingDetail, referralLetterDate } from './pdfHelpers'

export function mapUnspecifiedReferralToPdf(
  form: UnspecifiedReferralFormState,
  auto: AutoPopulateData,
  now: Date = new Date(),
): OrderPdfData {
  const icd10Codes = form.diagnosisText
    ? form.diagnosisText
        .split(/[;\n]/)
        .map((s) => s.trim())
        .filter(Boolean)
    : []

  // "Referral For" renders ABOVE the diagnosis section
  const preDiagnosisSections: OrderPdfSection[] = []
  if (form.referralFor) {
    preDiagnosisSections.push({ title: 'Referral For', type: 'text', content: form.referralFor })
  }
  if (form.majorComplaints) {
    preDiagnosisSections.push({
      title: 'Major Complaints',
      type: 'text',
      content: form.majorComplaints,
    })
  }

  const vendorSections: OrderPdfSection[] = []

  if (form.specialInstructions) {
    vendorSections.push({
      title: 'Special Instructions',
      type: 'text',
      content: form.specialInstructions,
    })
  }
  if (form.referringDoctorComments) {
    vendorSections.push({
      title: "Referring Doctor's Comments",
      type: 'text',
      content: form.referringDoctorComments,
    })
  }
  if (form.summary) {
    vendorSections.push({
      title: 'Clinical Summary',
      type: 'text',
      content: form.summary,
    })
  }

  return {
    vendorName: 'Referral',
    orderTitle: 'Patient Referral',
    // Suppress the "{vendor} — {title}" subtitle (redundant for referrals)
    subtitle: '',
    patient: {
      firstName: auto.patient.firstName,
      middleName: auto.patient.middleName,
      lastName: auto.patient.lastName,
      dateOfBirth: auto.patient.dateOfBirth,
      sex: auto.patient.sexCode,
      // Patient ID and email intentionally omitted from the referral PDF
      email: '',
      phone: auto.patient.phone,
      address: auto.patient.address,
      city: auto.patient.city,
      state: auto.patient.state,
      zip: auto.patient.zip,
      country: auto.patient.country,
      patientId: '',
    },
    provider: {
      organizationName: auto.provider.organizationName,
      providerName: auto.provider.providerName,
      npi: auto.provider.npi,
      phone: auto.provider.orgPhone,
      fax: auto.provider.orgFax,
      address: auto.provider.orgAddress,
      city: auto.provider.orgCity,
      state: auto.provider.orgState,
      zip: auto.provider.orgZip,
      email: auto.provider.providerEmail,
    },
    insurance: {
      primaryCarrier: auto.insurance.primaryCarrier,
      primaryMemberId: auto.insurance.primaryMemberId,
      primaryGroup: auto.insurance.primaryGroup,
      policyholderName: auto.insurance.policyholderName,
      relationship: auto.insurance.relationship,
    },
    icd10Codes,
    // diagnosisText intentionally omitted — icd10Codes bullets are the single source of truth
    // (passing both would render the codes twice)
    selectedTests: [],
    preDiagnosisSections,
    vendorSections,
    letter: form.indication.trim()
      ? {
          date: referralLetterDate(now),
          body: form.indication,
          closingName: auto.provider.providerName,
          closingDetail: referralClosingDetail(auto.provider),
        }
      : undefined,
    signatureDataUrl: form.providerSignatureDataUrl,
    signatureDate: form.providerSignatureDate,
  }
}
